import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import Home from "../views/Home.vue";
import store from "@/store/store";
import { Ref, ref } from "vue";
import { isRecord } from "@/utils/isRecord";
import { checkMaintenanceMode } from "@/utils/checkMaintenanceMode";

export const routes: Ref<Array<RouteRecordRaw>> = ref([
  {
    path: "/",
    name: "home",
    meta: {
      label: "ホーム",
      icon: "mdi-home",
    },
    component: Home,
  },
  {
    path: "/import",
    name: "import",
    meta: {
      label: "インポート",
      icon: "mdi-database-import",
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/Import.vue"),
  },
  {
    path: "/search",
    name: "search",
    meta: {
      label: "検索",
      icon: "mdi-magnify",
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/Search.vue"),
  },
  {
    path: "/quick-search",
    name: "quickSearch",
    meta: {
      label: "クイック検索",
      icon: "mdi-magnify-plus-outline",
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/QuickSearch.vue"),
  },
  {
    path: "/query",
    name: "queryList",
    meta: {
      label: "保存した検索条件",
      icon: "mdi-format-list-bulleted",
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/QueryList.vue"),
  },
  {
    path: "/calendar",
    name: "calendar",
    meta: {
      label: "カレンダー",
      icon: "mdi-calendar",
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/Calendar.vue"),
  },
  {
    path: "/notes-radar",
    name: "notesRadar",
    meta: {
      label: "ノーツレーダー",
      icon: "mdi-hexagon-outline",
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/NotesRadar.vue"),
  },
  {
    path: "/tower",
    name: "tower",
    meta: {
      label: "タワー",
      icon: "mdi-poll",
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/Tower.vue"),
  },
  {
    path: "/music/list",
    name: "musicList",
    meta: {
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/MusicList.vue"),
  },
  {
    path: "/statistics",
    name: "statistics",
    meta: {
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/Statistics.vue"),
  },
  {
    path: "/music/view",
    name: "musicView",
    meta: {
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/MusicView.vue"),
  },
  {
    path: "/user/:uid",
    name: "user",
    meta: {
      label: "プロフィール",
      icon: "mdi-account",
      requiresAuth: true,
      params: { uid: null },
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/User.vue"),
  },
  {
    path: "/user/:uid",
    name: "user",
    meta: {
      icon: "mdi-account",
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/User.vue"),
  },
  {
    path: "/user-edit",
    name: "userEdit",
    meta: {
      label: "プロフィール編集",
      icon: "mdi-account-edit",
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/UserEdit.vue"),
  },
  {
    path: "/user-search",
    name: "userSearch",
    meta: {
      label: "ユーザー検索",
      icon: "mdi-account-search",
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/UserSearch.vue"),
  },
  {
    path: "/settings",
    name: "settings",
    meta: {
      label: "設定",
      icon: "mdi-cog",
      requiresAuth: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/Settings.vue"),
  },
  {
    path: "/admin",
    name: "admin",
    meta: {
      label: "管理者画面",
      icon: "mdi-wrench",
      requiresAuth: true,
      requiresAdmin: true,
    },
    component: () => import(/* webpackChunkName: "about" */ "../views/Admin.vue"),
  },
  {
    path: "/privacy-policy",
    name: "privacyPolicy",
    meta: {},
    component: () => import(/* webpackChunkName: "about" */ "../views/PrivacyPolicy.vue"),
  },
  {
    path: "/terms",
    name: "terms",
    meta: {},
    component: () => import(/* webpackChunkName: "about" */ "../views/Terms.vue"),
  },
  {
    path: "/maintenance",
    name: "maintenance",
    meta: {},
    component: () => import(/* webpackChunkName: "about" */ "../views/Maintenance.vue"),
  },
  {
    path: "/:catchAll(.*)",
    name: "404",
    meta: {},
    component: () => import(/* webpackChunkName: "about" */ "../views/404.vue"),
  },
]);

store.dispatch("loading/wrapOverlayLoading", () =>
  store.dispatch("auth/fetchUser").then((user) => {
    const userRoute = routes.value.find(
      (e: RouteRecordRaw) => isRecord(e.meta?.params) && e.meta?.params.uid !== undefined
    );
    if (userRoute !== undefined && userRoute.meta !== undefined && isRecord(userRoute.meta.params)) {
      userRoute.meta.params.uid = user?.uid;
    }
  })
);

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: routes.value,
  scrollBehavior(to, from, savedPosition) {
    if (from.path === "/music/view" && ["/music/list", "/quick-search"].includes(to.path) && savedPosition !== null) {
      return new Promise<typeof savedPosition>((resolve) => {
        setTimeout(() => {
          resolve(savedPosition);
        });
      });
    }
    if (from.path === to.path) {
      return undefined;
    }
    return { left: 0, top: 0 };
  },
});

router.beforeEach(async (to) => {
  try {
    const userPromise = store.dispatch("auth/fetchUser");

    const isMaintenanceMode = await checkMaintenanceMode();
    const toMaintenance = to.path === "/maintenance";

    if (isMaintenanceMode && toMaintenance) {
      return true;
    }
    if (isMaintenanceMode) {
      return { path: "/maintenance" };
    }
    if (!isMaintenanceMode && toMaintenance) {
      return { path: "/" };
    }

    const user = await userPromise;

    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
    const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);

    if (requiresAdmin && !store.state.auth.claims?.admin) {
      return { path: "/" };
    }
    if (requiresAuth && !user) {
      return { path: "/" };
    }
    return true;
  } catch (e) {
    return true;
  }
});

export default router;
